import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getRequiredCurrentUserClientId } from "../../../core/auth/tenant";
import {
  buyPhoneNumber,
  getGreetingFileById,
  getGreetingUploadUrlById,
  getPurchasedPhoneNumbers,
  releasePhoneNumber,
  searchAvailablePhoneNumbers,
  uploadGreetingFileToPresignedUrl,
} from "../services/numbersService";
import {
  buildGreetingFileNameFromPhoneNumber,
  extractGreetingFileName,
  extractGreetingUploadUrl,
  extractGreetingUrl,
  getAvailableListFromResponse,
  getDynamicPageSize,
  getNumberManagementErrorMessage,
  getPurchasedListFromResponse,
  isLikelyTollFreeNumber,
  mapAvailableNumber,
  mapPurchasedNumber,
  toBuyNumberPayload,
  toSearchPayload,
} from "../services/numberMappers";

export const fetchPurchasedNumbersThunk = createAsyncThunk(
  "numbers/fetchPurchasedNumbers",
  async (clientId, { getState, rejectWithValue }) => {
    const resolvedClientId = Number(clientId) || getRequiredCurrentUserClientId(getState());
    const response = await getPurchasedPhoneNumbers(resolvedClientId, {
      limit: 100,
      offset: 0,
    });

    if (response?.error) {
      return rejectWithValue(
        getNumberManagementErrorMessage(response, "Failed to load purchased numbers."),
      );
    }

    return getPurchasedListFromResponse(response).map(mapPurchasedNumber);
  },
);

export const uploadGreetingThunk = createAsyncThunk(
  "numbers/uploadGreeting",
  async ({ number, file }, { getState, rejectWithValue }) => {
    if (!number?.id) {
      return rejectWithValue("Phone number id is missing.");
    }

    const targetFileName = buildGreetingFileNameFromPhoneNumber(number.phoneNumber);
    if (!targetFileName) {
      return rejectWithValue("Phone number is missing. Unable to name greeting file.");
    }

    const resolvedClientId = getRequiredCurrentUserClientId(getState());
    const uploadResponse = await getGreetingUploadUrlById(number.id, {
      client_id: resolvedClientId,
    });

    if (uploadResponse?.error) {
      return rejectWithValue(
        getNumberManagementErrorMessage(uploadResponse, "Failed to request upload URL."),
      );
    }

    const uploadUrl = extractGreetingUploadUrl(uploadResponse);

    if (!uploadUrl) {
      return rejectWithValue("Upload URL was not returned by the server.");
    }

    let uploadResult;
    try {
      const fileToUpload =
        file?.name === targetFileName
          ? file
          : new File([file], targetFileName, { type: "audio/mpeg" });

      uploadResult = await uploadGreetingFileToPresignedUrl(uploadUrl, fileToUpload);
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to upload greeting file.");
    }

    if (!uploadResult?.ok) {
      let errorDetails = "";
      try {
        errorDetails = await uploadResult.text();
      } catch (error) {
        errorDetails = "";
      }

      return rejectWithValue(
        errorDetails
          ? `Upload failed: ${errorDetails}`
          : "Failed to upload greeting file.",
      );
    }

    const greetingResponse = await getGreetingFileById(number.id);
    if (greetingResponse?.error) {
      return rejectWithValue(
        getNumberManagementErrorMessage(greetingResponse, "Greeting uploaded but not available yet."),
      );
    }

    const greetingUrl = extractGreetingUrl(greetingResponse);
    const greetingFileFromUrl = extractGreetingFileName(greetingUrl);
    const resolvedGreetingUrl =
      greetingUrl && (!greetingFileFromUrl || greetingFileFromUrl === targetFileName)
        ? greetingUrl
        : "";
    const greetingFile = targetFileName || greetingFileFromUrl || "-";

    return {
      numberId: number.id,
      greetingUrl: resolvedGreetingUrl,
      greetingFile,
    };
  },
);

export const searchAvailableNumbersThunk = createAsyncThunk(
  "numbers/searchAvailableNumbers",
  async (_, { getState, rejectWithValue }) => {
    const response = await searchAvailablePhoneNumbers(
      toSearchPayload(getState().numbers),
    );

    if (response?.error) {
      return rejectWithValue(
        getNumberManagementErrorMessage(response, "Failed to search phone numbers."),
      );
    }

    const numbers = getAvailableListFromResponse(response)
      .map((item, index) => mapAvailableNumber(item, index))
      .filter((item) => !isLikelyTollFreeNumber(item.phoneNumber, item.friendlyName));

    return {
      numbers,
      availablePageSize: getDynamicPageSize(response, numbers.length),
    };
  },
);

export const buyNumberThunk = createAsyncThunk(
  "numbers/buyNumber",
  async ({ number, clientId }, { dispatch, getState, rejectWithValue }) => {
    const resolvedClientId = Number(clientId) || getRequiredCurrentUserClientId(getState());
    if (!number || !number.phoneNumber || number.phoneNumber === "-") {
      return rejectWithValue("Unable to buy this number because phone number is missing.");
    }

    const response = await buyPhoneNumber(toBuyNumberPayload(number, resolvedClientId));

    if (response?.error) {
      return rejectWithValue(
        getNumberManagementErrorMessage(response, "Failed to buy and assign phone number"),
      );
    }

    await dispatch(fetchPurchasedNumbersThunk(resolvedClientId));

    return { phoneNumber: number.phoneNumber };
  },
);

export const releaseNumberThunk = createAsyncThunk(
  "numbers/releaseNumber",
  async ({ number, clientId }, { dispatch, getState, rejectWithValue }) => {
    const resolvedClientId = Number(clientId) || getRequiredCurrentUserClientId(getState());
    if (!number || !number.phoneNumber || number.phoneNumber === "-") {
      return rejectWithValue("Unable to release this number because phone number is missing.");
    }

    const response = await releasePhoneNumber(number.phoneNumber);

    if (response?.error) {
      return rejectWithValue(
        getNumberManagementErrorMessage(response, "Failed to release phone number."),
      );
    }

    await dispatch(fetchPurchasedNumbersThunk(resolvedClientId));
    return { success: true };
  },
);

const initialState = {
  selectedCountry: "US",
  searchCriteria: "number",
  searchDigits: "",
  matchTo: "",
  availableNumbers: [],
  availablePageSize: 10,
  purchasedNumbers: [],
  availableLoading: false,
  purchasedLoading: false,
  actionLoading: false,
  availableSpread: false,
  purchasedSpread: false,
  error: null,
};

export const numbersInitialState = initialState;

const numbersSlice = createSlice({
  name: "numbers",
  initialState,
  reducers: {
    setSelectedCountry: (state, action) => {
      state.selectedCountry = action.payload;
    },
    setSearchCriteria: (state, action) => {
      state.searchCriteria = action.payload;
    },
    setSearchDigits: (state, action) => {
      state.searchDigits = action.payload;
    },
    setMatchTo: (state, action) => {
      state.matchTo = action.payload;
    },
    resetSearchForm: (state) => {
      state.selectedCountry = "US";
      state.searchCriteria = "number";
      state.searchDigits = "";
      state.matchTo = "";
      state.availableNumbers = [];
      state.availablePageSize = 10;
    },
    toggleAvailableSpread: (state) => {
      state.availableSpread = !state.availableSpread;
    },
    togglePurchasedSpread: (state) => {
      state.purchasedSpread = !state.purchasedSpread;
    },
    uploadGreetingLocally: (state, action) => {
      const { numberId, fileName } = action.payload;
      state.purchasedNumbers = state.purchasedNumbers.map((item) =>
        item.id === numberId
          ? { ...item, greetingFile: fileName, hasGreeting: true }
          : item,
      );
    },
    updateGreetingMetadata: (state, action) => {
      const { numberId, greetingUrl, greetingFile } = action.payload;
      state.purchasedNumbers = state.purchasedNumbers.map((item) =>
        item.id === numberId
          ? {
              ...item,
              greetingUrl: greetingUrl || item.greetingUrl,
              greetingFile: greetingFile || item.greetingFile,
              hasGreeting: Boolean(greetingUrl || greetingFile || item.greetingFile),
            }
          : item,
      );
    },
    clearNumbersError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPurchasedNumbersThunk.pending, (state) => {
        state.purchasedLoading = true;
        state.error = null;
      })
      .addCase(fetchPurchasedNumbersThunk.fulfilled, (state, action) => {
        state.purchasedLoading = false;
        state.purchasedNumbers = action.payload;
      })
      .addCase(fetchPurchasedNumbersThunk.rejected, (state, action) => {
        state.purchasedLoading = false;
        state.error = action.payload || "Failed to load purchased numbers.";
      })
      .addCase(searchAvailableNumbersThunk.pending, (state) => {
        state.availableLoading = true;
        state.error = null;
      })
      .addCase(searchAvailableNumbersThunk.fulfilled, (state, action) => {
        state.availableLoading = false;
        state.availableNumbers = action.payload.numbers;
        state.availablePageSize = action.payload.availablePageSize;
      })
      .addCase(searchAvailableNumbersThunk.rejected, (state, action) => {
        state.availableLoading = false;
        state.error = action.payload || "Failed to search phone numbers.";
      })
      .addCase(buyNumberThunk.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(buyNumberThunk.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.availableNumbers = state.availableNumbers.filter(
          (item) => item.phoneNumber !== action.payload.phoneNumber,
        );
      })
      .addCase(buyNumberThunk.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Failed to buy and assign phone number";
      })
      .addCase(releaseNumberThunk.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(releaseNumberThunk.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(releaseNumberThunk.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Failed to release phone number.";
      })
      .addCase(uploadGreetingThunk.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(uploadGreetingThunk.fulfilled, (state, action) => {
        state.actionLoading = false;
        const { numberId, greetingUrl, greetingFile } = action.payload || {};
        state.purchasedNumbers = state.purchasedNumbers.map((item) =>
          item.id === numberId
            ? {
                ...item,
                greetingUrl: greetingUrl || "",
                greetingFile: greetingFile || item.greetingFile,
                hasGreeting: Boolean(greetingUrl || greetingFile),
              }
            : item,
        );
      })
      .addCase(uploadGreetingThunk.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Failed to upload greeting file.";
      });
  },
});

export const {
  clearNumbersError,
  resetSearchForm,
  setMatchTo,
  setSearchCriteria,
  setSearchDigits,
  setSelectedCountry,
  toggleAvailableSpread,
  togglePurchasedSpread,
  uploadGreetingLocally,
  updateGreetingMetadata,
} = numbersSlice.actions;

export const selectNumbersState = (state) => state.numbers;

export default numbersSlice.reducer;
